import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { AuthService, DataService, NPSService } from '@shared/services';
import { UserRequestsService } from '@shared/services/user-requests.service';
import { ToastrService } from 'ngx-toastr';
import { UserRequest } from '@case-editor/models';


export interface VotumModalData {
  request: UserRequest;
  votumModalTabs: any[];
  txtSizeValL: number;
  onRegenerate?: () => void;
}

@Component({
  selector: 'app-votum-modal',
  templateUrl: './votum-modal.component.html',
  styleUrl: './votum-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule
  ],
  host: {
    class: 'votum-modal'
  }
})
export class VotumModalComponent implements OnInit {
  public readonly bsModalRef = inject(BsModalRef);
  public readonly authService = inject(AuthService);
  private readonly dataService = inject(DataService);
  private readonly userRequestsService = inject(UserRequestsService);
  private readonly toastr = inject(ToastrService);

  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly npsService = inject(NPSService);

  // Data passed from parent component
  data: VotumModalData = {
    request: {} as UserRequest,
    votumModalTabs: [],
    txtSizeValL: 12
  };

  isVotumEditMode = false;
  editedVotum: any = {};
  isRegenerating = false;
  activeTabValue: string = '';

  // Note-Tab: Ansicht mit/ohne Zusatzpunkte (nur wenn Bonus/Malus vergeben wurde)
  zusatzView: 'mit' | 'ohne' = 'mit';

  // NPS Survey state
  showNPSSurvey = false;
  selectedRating = 0;
  npsComment = '';
  isSubmittingNPS = false;
  hasSubmittedNPS = false;

  // Filter tabs based on user permissions
  get filteredTabs() {
    if (!this.data.votumModalTabs) return [];

    // Filter out 'formalia' tab for non-admins
    return this.data.votumModalTabs.filter(tab => {
      if (tab.value === 'formalia') {
        return this.authService.isAdmin();
      }
      return true;
    });
  }

  async ngOnInit() {
    // Initialize edited votum when component loads (but don't enable edit mode)
    if (this.data.request?.votum && typeof this.data.request.votum === 'object') {
      this.editedVotum = { ...this.data.request.votum };
    }

    // Set the first tab as active by default (from filtered tabs)
    if (this.filteredTabs && this.filteredTabs.length > 0) {
      const activeTab = this.filteredTabs.find(tab => tab.selected);
      this.activeTabValue = activeTab ? activeTab.value : this.filteredTabs[0].value;
    }

    // Check if we should show NPS survey
    if (this.data.request?.id) {
      const shouldShow = await this.npsService.shouldShowNPSSurvey(this.data.request.id);
      this.showNPSSurvey = shouldShow;
      this.cdRef.markForCheck();
    }
  }

  enableVotumEdit() {
    const votum = this.data.request?.votum;
    if (votum && typeof votum === 'object' && this.hasVotumContent(votum)) {
      this.editedVotum = { ...votum };
      this.isVotumEditMode = true;
    }
  }

  private hasVotumContent(votum: any): boolean {
    // Check if votum has any meaningful content (not just empty strings)
    const fields = ['ausblick', 'erw_horizont', 'formalia', 'inhalt', 'note'];
    return fields.some(field => votum[field] && votum[field].trim() !== '');
  }

  cancelVotumEdit() {
    this.isVotumEditMode = false;
    this.editedVotum = {};
  }

  async saveVotumEdit() {
    const req = this.data.request;
    if (!req || !this.editedVotum) return;

    try {
      // Update the request with the edited votum
      const updatedRequest = { ...req, votum: this.editedVotum } as UserRequest;

      // Save to backend
      await this.dataService.updateUserRequest(req.id, { votum: this.editedVotum });

      // Update the local state
      this.userRequestsService.setUserRequest(updatedRequest);

      // Only reset edit mode on successful save
      this.isVotumEditMode = false;
      this.editedVotum = {};

      this.toastr.success('Votum erfolgreich aktualisiert');
    } catch (error) {
      console.error('Error saving votum:', error);
      // Keep edit mode active so user can retry
      this.isVotumEditMode = true;
      this.toastr.error('Fehler beim Speichern des Votums');
    }
  }

  switchTab(tab: any) {
    this.activeTabValue = tab.value;
    this.filteredTabs.forEach(item => item.value === tab.value ? item.selected = true : item.selected = false);
  }

  /**
   * Zusatzpunkte-Ansicht für den Note-Tab: liefert beide Notenperspektiven,
   * wenn der Lauf Bonus/Malus vergeben hat (Felder + Transparenzsatz aus dem
   * Backend), sonst null — dann wird die Note normal angezeigt.
   */
  get zusatzInfo(): { bonus: number; malus: number; mitText: string; ohneText: string } | null {
    const votum: any = this.data.request?.votum;
    if (!votum || typeof votum !== 'object') return null;
    const bonus = Number(votum.zusatz_bonus) || 0;
    const malus = Number(votum.zusatz_malus) || 0;
    if (!bonus && !malus) return null;

    const note = String(votum.note || '');
    const match = note.match(/Ohne Zusatzpunkte \(nur Bewertungsschablone: ([\d.,]+) von ([\d.,]+)\) laege die Bewertung bei ([\d.,]+) Punkten/);
    if (!match) return null;

    const mitText = note.split(/\s*Ohne Zusatzpunkte \(nur Bewertungsschablone/)[0].trim();
    const ohneText = `Nur nach der Bewertungsschablone — ohne Zusatzpunkte — wäre die Leistung mit ${match[3]} Punkten zu bewerten. (Erreichte Punkte: ${match[1]} von ${match[2]})`;
    return { bonus, malus, mitText, ohneText };
  }

  getFormattedVotumContent(section: string): string {
    const votum = this.data.request?.votum;
    if (votum && typeof votum === 'object') {
      const raw = (votum as any)[section];
      if (!raw) {
        return `<em class="text-muted">Kein Inhalt für ${section} verfügbar.</em>`;
      }
      // Backend returns clean text, just return it directly
      if (!raw.trim()) {
        return `<em class="text-muted">Inhalt für ${section} konnte nicht verarbeitet werden.</em>`;
      }
      return raw;
    }
    return '<em class="text-muted">Votum-Daten nicht verfügbar.</em>';
  }

  async regenerateVotum() {
    // Prevent multiple simultaneous regenerations
    if (this.isRegenerating) {
      return;
    }

    try {
      this.isRegenerating = true;
      this.cdRef.markForCheck();

      // First, clear the votum from the current request to trigger loading state
      const updatedRequest = { ...this.data.request, votum: undefined } as unknown as UserRequest;
      this.userRequestsService.setUserRequest(updatedRequest);

      // Close the current modal
      this.bsModalRef.hide();

      // Call parent component's regenerate callback if provided
      if (this.data.onRegenerate) {
        this.data.onRegenerate();
      } else {
        // Fallback: direct regeneration
        await this.dataService.sendVotum(this.data.request.id);
        this.toastr.info('Votum wird neu generiert...', '', { timeOut: 3000 });
      }
    } catch (error) {
      console.error('Error regenerating votum:', error);
      this.toastr.error('Fehler beim Neu-Generieren des Votums');

      // Restore the votum if there was an error
      this.userRequestsService.setUserRequest(this.data.request);
      this.cdRef.markForCheck();
    } finally {
      // Reset flag after a short delay (modal is closing anyway)
      setTimeout(() => {
        this.isRegenerating = false;
        this.cdRef.markForCheck();
      }, 1000);
    }
  }

  close() {
    this.bsModalRef.hide();
  }

  // NPS Survey methods
  selectRating(rating: number) {
    this.selectedRating = rating;
    this.cdRef.markForCheck();
  }

  async submitNPSRating() {
    if (this.selectedRating === 0 || this.isSubmittingNPS) {
      return;
    }

    this.isSubmittingNPS = true;
    this.cdRef.markForCheck();

    // Get case ID from request
    const caseData = (this.data.request as any)?.expand?.case || (this.data.request as any)?.case;
    let caseId = caseData?.id;
    if (!caseId && typeof caseData === 'string') {
      caseId = caseData;
    }

    if (!caseId) {
      console.error('Cannot submit NPS: case ID not found');
      this.isSubmittingNPS = false;
      return;
    }

    const success = await this.npsService.submitRating(
      this.data.request.id,
      caseId,
      this.selectedRating,
      this.npsComment.trim() || undefined
    );

    this.isSubmittingNPS = false;

    if (success) {
      this.hasSubmittedNPS = true;
      this.showNPSSurvey = false;
      this.toastr.success('Vielen Dank für deine Bewertung!');
      this.cdRef.markForCheck();
    }
  }

  skipNPSSurvey() {
    this.showNPSSurvey = false;
    this.cdRef.markForCheck();
  }

  getRatingLabel(rating: number): string {
    const labels = [
      'Nicht hilfreich',
      'Wenig hilfreich',
      'Okay',
      'Hilfreich',
      'Sehr hilfreich'
    ];
    return labels[rating - 1] || '';
  }

  getCommentPlaceholder(): string {
    if (this.selectedRating <= 2) {
      return 'Was können wir verbessern?';
    } else if (this.selectedRating === 3) {
      return 'Was hat dir gefallen? Was können wir besser machen?';
    } else {
      return 'Was hat dir besonders gut gefallen?';
    }
  }
}
